import { Package, ChevronLeft, ChevronRightIcon, ChevronDown, ChevronRight } from 'lucide-react';
import { Category } from '../../../api/protosCompiled/buildFoundry/buildFoundry_pb';
import { FoundryItem } from '../../../api/protosCompiled/buildFoundry/buildFoundry_pb';

import {
  Box,
  Typography,
  IconButton,
  useTheme,
  alpha,
  CircularProgress,
  List,
  ListItem,
  Tooltip,
  Button,
} from '@mui/material';
import { useState, useCallback } from 'react';
import { Plus } from 'lucide-react';
import { useStore } from 'zustand';
import { useAssemblyStore } from '../../../stores/AssemblyStore';
import { useUnityBuildStore } from '../../../stores/store';
import { useFoundrySelectionStore } from '../../../stores/foundrySelection.store';
import { ItemDetailsPanel } from './ItemDetailsPanel';
import { instructionData } from '../../instructions/instructionData';
import { useAssemblyRowActions } from '../../accessManager';

// Items List Component
interface ItemsListProps {
  items: FoundryItem.AsObject[];
  selectedItems: Set<string>;
  onItemSelect: (itemId: string) => void;
  onSelectAll: () => void;
  onClearSelection: () => void;
  onMoveItems: (targetCategoryId: string) => void;
  categories: Category.AsObject[];
  isLoading: boolean;
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  searchTerm?: string;
  currentCategoryPath?: string;
}

export const ItemsList: React.FC<ItemsListProps> = ({
  items,
  selectedItems,
  onItemSelect,
  onSelectAll,
  onClearSelection,
  onMoveItems,
  categories,
  isLoading,
  currentPage,
  totalPages,
  onPageChange,
  searchTerm,
  currentCategoryPath,
}) => {
  const theme = useTheme();
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Help system state
  const setIsHelpOpen = useStore(useUnityBuildStore, state => state.setIsHelpOpen);

  // Assembly state
  const assemblyOption = useStore(useAssemblyStore, state => state.assemblyOption);
  const currentGlobalAssembly = useStore(useAssemblyStore, state => state.currentGlobalAssembly);
  const currentTakeoffAssembly = useStore(useAssemblyStore, state => state.currentTakeoffAssembly);
  const setCurrentGlobalAssembly = useStore(
    useAssemblyStore,
    state => state.setCurrentGlobalAssembly
  );
  const setCurrentTakeoffAssembly = useStore(
    useAssemblyStore,
    state => state.setCurrentTakeoffAssembly
  );
  const setFoundryCategoryForItem = useFoundrySelectionStore(
    state => state.setFoundryCategoryForItem
  );
  const selectedBreakout = useStore(useUnityBuildStore, state => state.selectedBreakout);

  const canAddToAssembly =
    (assemblyOption === 'global' && !!currentGlobalAssembly) ||
    (assemblyOption === 'takeoff' && !!currentTakeoffAssembly);

  const { handleDisableRowActions } = useAssemblyRowActions();

  const handleAddFoundryItem = useCallback(
    (e: React.MouseEvent, item: FoundryItem.AsObject) => {
      e.stopPropagation();
      if (!canAddToAssembly) return;

      if (assemblyOption === 'global' && currentGlobalAssembly) {
        setCurrentGlobalAssembly(prev => {
          if (!prev) return null;
          return {
            ...prev,
            itemsList: [
              ...(prev.itemsList || []),
              {
                itemid: item.id,
                itemname: item.name,
                isquoted: false,
                isfoundryitem: true,
                foundrycategorypath: currentCategoryPath || '', // Store the category path
              },
            ],
          };
        });
      } else if (assemblyOption === 'takeoff' && currentTakeoffAssembly) {
        setCurrentTakeoffAssembly(prev => {
          if (!prev) return null;
          return {
            ...prev,
            itemsList: [
              ...(prev.itemsList || []),
              {
                itemid: item.id,
                itemname: item.name,
                itemqtyformula: {
                  formulaid: '',
                  itemid: item.id,
                  isquoted: false,
                  attributevalueid: 0,
                  quotegroupid: '',
                  itemqty: 1,
                  forevery: 1,
                  takeoffvariabletype: 'point',
                  and: false,
                  or: false,
                },
                isquoted: false,
                isfoundryitem: true, // Add the missing isfoundryitem property
                attribute: undefined,
                quotegroup: { id: '', quotegroup: '' },
                breakout: {
                  breakoutmapid: '',
                  breakoutid: selectedBreakout?.id || '',
                  breakoutname: selectedBreakout?.breakoutname || '',
                  multiplier: selectedBreakout?.multiplier || 1,
                  typicalbreakoutid: (selectedBreakout as any)?._typicalBreakoutId || '',
                },
                foundrycategorypath: currentCategoryPath || '', // Store the category path
              },
            ],
          };
        });
      }
      if (currentCategoryPath) {
        setFoundryCategoryForItem(item.id, currentCategoryPath);
      }
    },
    [
      canAddToAssembly,
      setCurrentGlobalAssembly,
      setCurrentTakeoffAssembly,
      currentTakeoffAssembly,
      assemblyOption,
      currentGlobalAssembly,
      setFoundryCategoryForItem,
      currentCategoryPath,
      selectedBreakout,
    ]
  );

  const toggleExpanded = (itemId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const isExpanded = (itemId: string) => expandedItems.has(itemId);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={32} sx={{ mb: 1 }} />
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {searchTerm ? `Searching for "${searchTerm}"...` : 'Loading items...'}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: theme.palette.background.paper,
      }}
    >
      {/* Items List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {items.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Box
              sx={{
                textAlign: 'center',
                color: theme.palette.text.secondary,
                px: 3,
                py: 4,
                maxWidth: '600px',
                margin: '0 auto',
              }}
            >
              {/* Main Heading */}
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: theme.palette.primary.main,
                  textShadow: `0 0 20px ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              >
                Database in Development
              </Typography>

              {/* Info Box */}
              <Box
                sx={{
                  p: 3,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.info.main, 0.1),
                  border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                  mb: 3,
                }}
              >
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                  Items are available to add to assemblies, but may not have pricing or labor data
                  yet.
                </Typography>

                <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                  You can use override prices for these items in the closeout screen, though they
                  won't be persistent yet.
                </Typography>

                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  We're implementing persistent labor and pricing overrides that will contribute to
                  community values for these items.
                </Typography>

                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    // Find the Community Items section index and open help
                    const communityItemsIndex = instructionData.findIndex(
                      instruction => instruction.title === '🌍 Community Items'
                    );
                    if (communityItemsIndex !== -1) {
                      // Store the target section index in localStorage for the help system to read
                      localStorage.setItem('helpTargetSection', communityItemsIndex.toString());
                      setIsHelpOpen(true);
                    }
                  }}
                  sx={{
                    mt: 2,
                    color: theme.palette.info.main,
                    borderColor: theme.palette.info.main,
                    '&:hover': {
                      borderColor: theme.palette.info.dark,
                      backgroundColor: alpha(theme.palette.info.main, 0.08),
                    },
                  }}
                >
                  Learn More
                </Button>
              </Box>

              {/* Status Info */}
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.background.paper, 0.5),
                  border: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
                }}
              >
                {searchTerm ? (
                  <>
                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5 }}>
                      No items found matching "{searchTerm}"
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.6 }}>
                      Try adjusting your search terms
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2" sx={{ opacity: 0.8, mb: 0.5 }}>
                      No items in this folder yet
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.6 }}>
                      Components will be available soon
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {items.map(item => (
              <Box key={item.id}>
                <ListItem
                  sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: alpha(theme.palette.action.hover, 0.1),
                    },
                    ...(selectedItems.has(item.id) && {
                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                      '&:hover': {
                        bgcolor: alpha(theme.palette.primary.main, 0.15),
                      },
                    }),
                  }}
                  onClick={() => onItemSelect(item.id)}
                >
                  {/* Add to Assembly Button (replaces checkbox) */}
                  <Tooltip
                    title={
                      canAddToAssembly ? 'Add to assembly' : 'Open a global assembly to add items'
                    }
                  >
                    <span>
                      <IconButton
                        size="small"
                        onClick={e => handleAddFoundryItem(e, item)}
                        disabled={!canAddToAssembly || handleDisableRowActions()}
                        sx={{
                          mr: 1.5,
                          color: theme.palette.text.secondary,
                          '&:hover': {
                            color: theme.palette.primary.main,
                            bgcolor: alpha(theme.palette.primary.main, 0.08),
                          },
                          '&.Mui-disabled': {
                            opacity: 0.5,
                          },
                        }}
                      >
                        <Plus size={18} />
                      </IconButton>
                    </span>
                  </Tooltip>

                  {/* Expand/Collapse Chevron */}
                  <IconButton
                    onClick={e => toggleExpanded(item.id, e)}
                    sx={{
                      p: 0.5,
                      mr: 1,
                      color: theme.palette.text.secondary,
                      '&:hover': {
                        color: theme.palette.text.primary,
                        bgcolor: alpha(theme.palette.action.hover, 0.1),
                      },
                    }}
                  >
                    {isExpanded(item.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </IconButton>

                  {/* Item Image */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: 32,
                      height: 32,
                      mr: 1.5,
                      borderRadius: 1,
                      overflow: 'hidden',
                      border: `1px solid ${theme.palette.divider}`,
                      bgcolor: theme.palette.background.default,
                    }}
                  >
                    {item.imageUrl ? (
                      <Box
                        component="img"
                        src={item.imageUrl}
                        alt={item.name}
                        sx={{
                          width: 32,
                          height: 32,
                          objectFit: 'cover',
                          transition: 'all 0.3s',
                          '&:hover': {
                            width: 48,
                            height: 48,
                            zIndex: 20,
                            boxShadow: theme.shadows[8],
                            position: 'relative',
                          },
                        }}
                        onError={e => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                    ) : (
                      <Package size={20} style={{ color: theme.palette.text.secondary }} />
                    )}
                  </Box>

                  {/* Item Details */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        ...(selectedItems.has(item.id) && {
                          fontWeight: 600,
                          color: theme.palette.primary.main,
                        }),
                      }}
                    >
                      {item.name}
                    </Typography>
                  </Box>
                </ListItem>
                {/* Item Details Panel */}
                <ItemDetailsPanel itemId={item.id} isExpanded={isExpanded(item.id)} item={item} />
              </Box>
            ))}
          </List>
        )}
      </Box>

      {/* Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            flexShrink: 0,
            p: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
              Page {currentPage} of {totalPages}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1}
                sx={{
                  color: theme.palette.text.disabled,
                  '&:hover': {
                    color: theme.palette.text.primary,
                  },
                  '&.Mui-disabled': {
                    opacity: 0.5,
                  },
                }}
              >
                <ChevronLeft size={16} />
              </IconButton>
              <IconButton
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                sx={{
                  color: theme.palette.text.disabled,
                  '&:hover': {
                    color: theme.palette.text.primary,
                  },
                  '&.Mui-disabled': {
                    opacity: 0.5,
                  },
                }}
              >
                <ChevronRightIcon size={16} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};
